import { Box, Button, Checkbox, Text } from '@wix/design-system'
import { ChoiceQuestionContentComponent, GroupContentComponent, useContent, useNext } from '@wix/funnel-builder-lizard'
import classNames from 'classnames'
import React, { FC, useEffect, useState } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { WixMobileBackButton } from '../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileContentWrapper } from '../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileFooter } from '../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileSkipButton } from '../../reusable/WixMobileSkipButton/WixMobileSkipButton'
import { WixMobileStepProgressBar } from '../../reusable/WixMobileStepProgressBar/WixMobileStepProgressBar'
import { wixMobileBaseCheckboxDataHooks as DataHooks } from './WixMobileBaseCheckbox.dataHooks'
import s from './WixMobileBaseCheckbox.scss'

type Item = {
  title: string
  biSlug: string
  optionKey: string
}

export type Content = {
  back: string
  next: string
  skip: string
  stepProgressBar: GroupContentComponent<{ visible: boolean; total: number; current: number }>
  question: ChoiceQuestionContentComponent<{ title: string }, Item>
}

export const WixMobileBaseCheckbox: FC = () => {
  const [values, setValues] = useState<string[]>([])
  const { content } = useContent<Content>()
  const next = useNext()
  const pageLogger = usePageLogger()

  const { title } = content.question.content

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const onToggle = (value: string) => {
    pageLogger.clickAnswer({
      questionKey: content.question.questionKey,
      buttonName: content.question.options.find(o => o.optionKey === value)?.biSlug || value,
    })
    setValues(values => (values.includes(value) ? values.filter(v => v !== value) : [...values, value]))
  }

  const onSkipButtonClick = () => {
    pageLogger.skip({
      payload: values,
    })

    next({ [content.question.questionKey]: { skipped: true } })
  }

  const onNextButtonClick = () => {
    pageLogger.answer({
      answer: values,
    })

    next({ [content.question.questionKey]: { values } })
  }

  const onBackButtonClick = () => {
    pageLogger.back({
      payload: values,
    })

    window.history.back()
  }

  return (
    <WixMobilePage>
      <WixMobileHeader
        leftContent={
          (content.back && (
            <WixMobileBackButton dataHook={DataHooks.back()} onBack={onBackButtonClick} label={content.back} />
          )) ||
          undefined
        }
        rightContent={
          (content.skip && (
            <WixMobileSkipButton dataHook={DataHooks.skip()} onSkip={onSkipButtonClick} label={content.skip} />
          )) ||
          undefined
        }
      />
      <WixMobileContentWrapper>
        {content.stepProgressBar.content.visible && (
          <WixMobileStepProgressBar
            current={content.stepProgressBar.content.current}
            total={content.stepProgressBar.content.total}
            dataHook={DataHooks.stepProgressBar()}
          />
        )}
        <WixMobileHeading dataHook={DataHooks.title()}>{title}</WixMobileHeading>
        <ul className={s.WixMobileBaseCheckbox}>
          {content.question.options.map(option => (
            <ItemComponent
              key={option.optionKey}
              item={option}
              isActive={values.includes(option.optionKey)}
              onClick={() => onToggle(option.optionKey)}
            />
          ))}
        </ul>
      </WixMobileContentWrapper>
      {content.next && (
        <WixMobileFooter>
          <Button
            size="large"
            fullWidth
            disabled={!values.length}
            onClick={onNextButtonClick}
            dataHook={DataHooks.next()}
          >
            {content.next}
          </Button>
        </WixMobileFooter>
      )}
    </WixMobilePage>
  )
}

type ItemProps = {
  isActive: boolean
  item: Item
  onClick: () => void
}

const ItemComponent: FC<ItemProps> = ({ isActive, onClick, item }) => {
  return (
    <li
      className={classNames(s.WixMobileBaseCheckbox__item, { [s['WixMobileBaseCheckbox__item--active']]: isActive })}
      onClick={onClick}
      data-hook={DataHooks.item({ key: item.optionKey })}
    >
      <div className={s.WixMobileBaseCheckbox__itemContent}>
        <Box className={s.WixMobileBaseCheckbox__itemTitle}>
          <Text weight="normal">{item.title}</Text>
        </Box>
      </div>
      <Checkbox checked={isActive} dataHook={DataHooks.itemCheckbox({ key: item.optionKey })} />
    </li>
  )
}
